import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import { Paper, Button, makeStyles, Box } from '@material-ui/core';
import {
    ConfiguredFields,
    DropDownField,
    IColumn,
    LicenseList,
    MergeTemplate,
    Order,
} from '../../../../store/modules/application/types';
import Loader from '../../../../components/Loader';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import { BASE_URL } from '../../../../routes';
import InfiniteScrollTableView from '../../../../components/InfiniteScrollTableView';
import { getFlatColumnFieldsFromList, getFormattedColumns } from 'src/utils/utils';

type PersonLicensesProps = {
    loading: boolean;
    skip: number;
    sortBy: string;
    hasMore: boolean;
    personId: string;
    sortOrder: Order;
    data: LicenseList;
    licenseCount: number;
    templateLoading: boolean;
    isAddLicenseModalOpen: boolean;
    mergeTemplatesList: MergeTemplate[];
    personLicenseColumnFields: ConfiguredFields;
    setSkip: Function;
    setSortOrder: Function;
    setSortColumn: Function;
    mergeDocuments: Function;
    handleAddLicense: Function;
    getMergeTemplatesBegin: Function;
};

const useStyles = makeStyles({
    addLicense: {
        color: '#0277BD',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    tablePaginationInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
    },
    countSpan: {
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
        display: 'flex',
        alignItems: 'center',
    },
});

const PersonLicenses: FunctionComponent<PersonLicensesProps> = (props) => {
    const {
        data,
        skip,
        sortBy,
        loading,
        setSkip,
        hasMore,
        sortOrder,
        setSortOrder,
        licenseCount,
        setSortColumn,
        mergeDocuments,
        templateLoading,
        handleAddLicense,
        mergeTemplatesList,
        getMergeTemplatesBegin,
        personLicenseColumnFields,
    } = props;

    const columns: IColumn[] = personLicenseColumnFields?.fields?.length
        ? getFormattedColumns(personLicenseColumnFields)
        : [
              {
                  id: 'licenseNumber|string',
                  label: 'License Number',
                  align: 'left',
                  maxWidth: 200,
                  field: 'licenseNumber',
                  sortFields: ['licenseNumber'],
              },
              {
                  id: 'licenseType|string',
                  label: 'License Type',
                  align: 'left',
                  maxWidth: 300,
                  field: 'type.name',
                  sortFields: ['type.name'],
              },
              {
                  id: 'status|string',
                  label: 'Status',
                  align: 'left',
                  maxWidth: 200,
                  field: 'status',
                  sortFields: ['status'],
              },
              {
                  id: 'issueDate|date',
                  label: 'Issue Date',
                  align: 'left',
                  maxWidth: 200,
                  field: 'issueDate',
                  sortFields: ['issueDate'],
              },
              {
                  id: 'expirationDate|date',
                  label: 'Expiration Date',
                  align: 'left',
                  maxWidth: 300,
                  field: 'expirationDate',
                  sortFields: ['expirationDate'],
              },
          ];

    const classes = useStyles();
    const history = useHistory();
    const [selectedPersonLicenses, setSelectedPersonLicenses] = useState<string[]>([]);

    useEffect(() => {
        const payload = {
            where: {
                type: 'License',
            },
            order: ['name ASC'],
            fields: {
                id: true,
                name: true,
                type: true,
            },
        };
        getMergeTemplatesBegin(payload);
    }, [getMergeTemplatesBegin]);

    const onClickRow = (id: number, e: any) => {
        e.stopPropagation();
        e.preventDefault();
        routeChange(BASE_URL + `/license/${id}/detail`);
    };

    const routeChange = (path: string) => {
        history.push(path);
    };

    if (loading) {
        return <Loader />;
    }

    const licenses = personLicenseColumnFields?.fields?.length
        ? getFlatColumnFieldsFromList(data, columns)
        : data.map((license: any) => {
              return {
                  id: license?.id,
                  licenseNumber: license?.licenseNumber,
                  licenseType: license?.type?.name,
                  status: license?.status,
                  issueDate: license?.issueDate,
                  expirationDate: license?.expirationDate,
              };
          });

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };

    const filteredTemplateList = mergeTemplatesList.map((template) => {
        return { label: template.name, value: template.id };
    });

    const handleCheckBoxClick = (selectedLicenses: string[]) => {
        setSelectedPersonLicenses(selectedLicenses);
    };

    const handleDownload = (selectedTemplate: DropDownField) => {
        const selectedTemplateName = filteredTemplateList.find((item) => item.value === selectedTemplate?.value)?.label;
        mergeDocuments(selectedTemplate?.value, selectedTemplateName, '', selectedPersonLicenses);
    };

    if (templateLoading) return <Loader />;
    return (
        <div className="verticalFillContainer">
            <div className="button-container">
                <Button
                    startIcon={<AddIcon />}
                    className={classes.addLicense}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                        handleAddLicense && handleAddLicense(e);
                    }}
                >
                    ADD LICENSE
                </Button>
            </div>
            <Box className={classes.tablePaginationInfo}>
                <span className={classes.countSpan}>{`${licenseCount} Results`}</span>
            </Box>

            <Paper className="case-entries-container verticalFillContent">
                {licenses?.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={licenses}
                        count={licenses.length}
                        onClickRow={onClickRow}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                        isTableRowSelectable={true}
                        additionalData={mergeTemplatesList}
                        errorMessage={'Select one or more licences'}
                        handleAction={handleDownload}
                        buttonLabel={'DOWNLOAD NOTICES'}
                        handleCheckIconClick={handleCheckBoxClick}
                    />
                ) : (
                    <TableEmptyState
                        title="No active records for this license."
                        message=" All your licenses will be stored here "
                    ></TableEmptyState>
                )}
            </Paper>
        </div>
    );
};

export default PersonLicenses;
